import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ADMIN_BUCKET_CHECK_FOLDERS_QUERY, ADMIN_BUCKET_CHECK_QUERY } from '../../graphql/queries';
import { ADMIN_DELETE_BUCKET_ORPHANS_MUTATION, ADMIN_START_BUCKET_CHECK_MUTATION } from '../../graphql/mutations';
import Pagination from '../../components/common/Pagination';
import {
  FACTOR_MIN_FILES, FACTOR_RATE_KEY, FACTOR_TOLERANCE, FactorAdvice, StorageFactorKey, factorAdvice,
} from '../../utils/storageFactors';
import styles from './BucketCheck.module.css';

interface Folder { path: string; gb: number; recordedGb: number | null; realGb: number | null; belongsTo: string; canDelete: boolean }
interface Factor { type: string; label: string; files: number; rawGb: number; derivedGb: number; measured: number; current: number }
interface CheckResult {
  objects: number; totalGb: number; recordedGb: number; versionsListed: boolean; noncurrentGb: number;
  byClass: { key: string; gb: number; usdMonthly: number }[]; unattributedGb: number;
  multipartUploads: number; multipartGb: number; multipartOldestAt: string | null;
  orphanCount: number; orphanGb: number; deletableCount: number; deletableGb: number;
  missingCount: number; missingGb: number;
  differsCount: number;
  factors: Factor[];
  warnings: { level: string; message: string }[];
}
interface Deletion {
  running: boolean; startedAt: string; finishedAt: string | null; total: number; done: number;
  deleted: number; deletedGb: number; skipped: number; failed: number; error: string | null;
}
interface Check {
  running: boolean; startedAt: string | null; finishedAt: string | null; objectsListed: number;
  error: string | null; result: CheckResult | null; deletion: Deletion | null;
}

const CLASS_LABEL: Record<string, string> = {
  STANDARD: 'Standard', INTELLIGENT_TIERING: 'Intelligent-Tiering', STANDARD_IA: 'Standard-IA',
  ONEZONE_IA: 'One Zone-IA', GLACIER_IR: 'Glacier Instant Retrieval', GLACIER: 'Glacier Flexible Retrieval',
  DEEP_ARCHIVE: 'Glacier Deep Archive',
};

const num = (n: number | null, dp = 1) => (n == null ? '—' : n.toLocaleString('en-US', { maximumFractionDigits: dp }));
const usd = (n: number) => `$${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '');

type ListKey = 'orphans' | 'missing' | 'differs';

const LISTS: { key: ListKey; title: string; columns: ('recorded' | 'real')[] }[] = [
  { key: 'orphans', title: 'In S3, not in the records', columns: ['real'] },
  { key: 'missing', title: 'In the records, not in S3', columns: ['recorded'] },
  { key: 'differs', title: 'Size differs', columns: ['recorded', 'real'] },
];

const FOLDERS_PAGE = 25;

interface OrphanActions {
  count: number;
  gb: number;
  busy: boolean;
  progress: number;
  onDelete: (target: { paths: string[] } | { all: true }) => void;
}

// One list at a time, a page at a time: a bucket can hold thousands of
// folders without a record, and the server keeps them all.
const FolderLists = ({ counts, orphans }: { counts: Record<ListKey, number>; orphans: OrphanActions }) => {
  const [list, setList] = useState<ListKey>('orphans');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, previousData, loading, error, refetch } = useQuery(ADMIN_BUCKET_CHECK_FOLDERS_QUERY, {
    variables: { list, page, limit: FOLDERS_PAGE, search: search || undefined },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (orphans.progress > 0) void refetch();
  }, [orphans.progress, refetch]);
  const result = (data ?? previousData)?.adminBucketCheckFolders as { folders: Folder[]; total: number } | undefined;
  const rows = result?.folders ?? [];
  const total = result?.total ?? 0;
  const { columns } = LISTS.find((l) => l.key === list)!;
  const deletable = list === 'orphans';

  const pick = (key: ListKey) => { setList(key); setPage(1); };

  return (
    <div className={styles.lists}>
      <div className={styles.tabs} role="tablist" aria-label="Folder lists">
        {LISTS.map((l) => (
          <button
            key={l.key}
            type="button"
            role="tab"
            aria-selected={list === l.key}
            className={`${styles.tab} ${list === l.key ? styles.tabActive : ''}`}
            onClick={() => pick(l.key)}
          >
            {l.title} <span className={styles.count}>{num(counts[l.key], 0)}</span>
          </button>
        ))}
      </div>
      {counts[list] === 0 ? (
        <p className={styles.muted}>None.</p>
      ) : (
        <>
          {deletable && (
            <div className={styles.toolbar}>
              <p className={styles.small}>
                Only a folder whose id names no file, note, project or user, and is a day old, can be
                deleted. A folder that belongs to a file is fixed by Recalculate storage on its owner.
              </p>
              {orphans.count > 0 && (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.danger}`}
                  disabled={orphans.busy}
                  onClick={() => {
                    if (window.confirm(`Delete ${num(orphans.count, 0)} folders (${num(orphans.gb, 2)} GB) that belong to no file, note, project or user? They are removed from S3 for good.`)) {
                      orphans.onDelete({ all: true });
                    }
                  }}
                >
                  <span className="material-symbols-outlined text-sm">delete</span>
                  Delete all {num(orphans.count, 0)} with no owner ({num(orphans.gb, 2)} GB)
                </button>
              )}
            </div>
          )}
          <input
            type="search"
            className={styles.search}
            placeholder="Search by folder or what it belongs to…"
            aria-label="Search folders"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {error && <p className={styles.warn} role="alert">The folders could not be loaded: {error.message}</p>}
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Folder</th><th>Belongs to</th>
                  {columns.includes('recorded') && <th>Recorded GB</th>}
                  {columns.includes('real') && <th>In S3 GB</th>}
                  {deletable && <th><span className={styles.srOnly}>Actions</span></th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.path}>
                    <td className={styles.path}>{f.path}</td>
                    <td>{f.belongsTo}</td>
                    {columns.includes('recorded') && <td>{num(f.recordedGb, 2)}</td>}
                    {columns.includes('real') && <td>{num(f.realGb, 2)}</td>}
                    {deletable && (
                      <td className={styles.actions}>
                        {f.canDelete && (
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.danger} ${styles.rowBtn}`}
                            disabled={orphans.busy}
                            aria-label={`Delete ${f.path}`}
                            onClick={() => {
                              if (window.confirm(`Delete ${f.path} from S3 (${num(f.realGb, 2)} GB)? This cannot be undone.`)) {
                                orphans.onDelete({ paths: [f.path] });
                              }
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && !error && rows.length === 0 && (
                  <tr><td colSpan={2 + columns.length + (deletable ? 1 : 0)} className={styles.muted}>No folder matches “{search}”.</td></tr>
                )}
                {loading && rows.length === 0 && (
                  <tr><td colSpan={2 + columns.length + (deletable ? 1 : 0)} className={styles.muted}>Loading…</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={FOLDERS_PAGE} total={total} onPageChange={setPage} noun="folders" variant="inline" />
        </>
      )}
    </div>
  );
};

/** The Unit prices form's storage factors: saved, as typed now, and how to put a value into it. */
export interface FactorControl {
  saved: Record<string, number>;
  inForm: Record<string, number> | null;
  onUse: (key: StorageFactorKey, value: number) => void;
}

const FactorSuggestion = ({ advice, onUse }: { advice: FactorAdvice; onUse?: () => void }) => {
  switch (advice.kind) {
    case 'none': return <span className={styles.muted}>No files yet</span>;
    case 'few': return <span className={styles.muted}>Too few files to judge ({num(advice.files, 0)})</span>;
    case 'keep':
      return (
        <span className={styles.adviceOk}>
          <span className="material-symbols-outlined text-sm" aria-hidden="true">check_circle</span>Keep
        </span>
      );
    case 'unsaved': return <span className={styles.adviceUnsaved}>In the form below, not saved yet</span>;
    default:
      return (
        <span className={styles.adviceChange}>
          Change to × {num(advice.value, 2)}
          {onUse && (
            <button type="button" className={`${styles.btn} ${styles.rowBtn}`} onClick={onUse}>
              Use × {num(advice.value, 2)}
            </button>
          )}
        </span>
      );
  }
};

const BucketCheck = ({ factorControl }: { factorControl?: FactorControl } = {}) => {
  const { data, error, refetch, startPolling, stopPolling } = useQuery(ADMIN_BUCKET_CHECK_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
  });
  const [startError, setStartError] = useState<string | null>(null);
  const [start, { loading: starting }] = useMutation(ADMIN_START_BUCKET_CHECK_MUTATION, {
    onCompleted: () => { setStartError(null); void refetch(); },
    onError: (e) => setStartError(e.message),
  });
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteOrphans, { loading: deleting }] = useMutation(ADMIN_DELETE_BUCKET_ORPHANS_MUTATION, {
    onCompleted: () => { setDeleteError(null); void refetch(); },
    onError: (e) => setDeleteError(e.message),
  });
  const check = data?.adminBucketCheck as Check | undefined;
  const running = !!check?.running;
  const deletion = check?.deletion ?? null;
  const busy = running || !!deletion?.running;

  useEffect(() => {
    if (!busy) return undefined;
    startPolling(3000);
    return () => stopPolling();
  }, [busy, startPolling, stopPolling]);

  const r = check?.result;
  const factorRows = (r?.factors ?? []).map((f) => {
    const key = FACTOR_RATE_KEY[f.type];
    const saved = key && factorControl ? factorControl.saved[key] ?? f.current : f.current;
    return { f, key, saved, advice: factorAdvice(f, saved, key ? factorControl?.inForm?.[key] : undefined) };
  });
  const toChange = factorRows.filter((x) => x.advice.kind === 'change');
  const unsaved = factorRows.filter((x) => x.advice.kind === 'unsaved');
  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <h3 className={styles.title}>Check the bucket</h3>
        <button className={styles.btn} onClick={() => { setStartError(null); void start(); }} disabled={busy || starting}>
          <span className="material-symbols-outlined text-sm">{running ? 'hourglass_top' : 'fact_check'}</span>
          {running || starting ? 'Checking…' : 'Check the bucket'}
        </button>
      </div>
      <p className={styles.note}>
        Lists the whole bucket once and compares it with the storage records. Nothing is saved and
        nothing above uses it: the result stays in the server&apos;s memory until the next check or a
        restart. A large bucket takes a few minutes.
      </p>
      {error && <p className={styles.warn} role="alert">The check could not be loaded: {error.message}</p>}
      {startError && <p className={styles.warn} role="alert">The check could not start: {startError}</p>}
      {running && (
        <p className={styles.progress} role="status">
          <span className="material-symbols-outlined animate-spin text-sm">progress_activity</span>
          Listing… {num(check?.objectsListed ?? 0, 0)} objects so far
        </p>
      )}
      {check?.error && !running && <p className={styles.warn}>The last check failed: {check.error}</p>}
      {!r && !running && !check?.error && <p className={styles.muted}>No check has run since the server started.</p>}

      {r && (
        <>
          <p className={styles.small}>
            {check?.finishedAt ? `Checked ${date(check.finishedAt)}` : `Started ${date(check?.startedAt ?? null)}`}
            {running && ' · showing the previous result until this one finishes'}
          </p>
          <div className={styles.stats}>
            <div className={styles.stat}>
              <p className={styles.statLabel}>In the bucket</p>
              <p className={styles.statValue}>{num(r.totalGb)} GB</p>
              <p className={styles.statHint}>{num(r.objects, 0)} objects</p>
            </div>
            <div className={styles.stat}>
              <p className={styles.statLabel}>In the records</p>
              <p className={styles.statValue}>{num(r.recordedGb)} GB</p>
              <p className={styles.statHint}>
                {r.totalGb - r.recordedGb >= 0 ? `${num(r.totalGb - r.recordedGb)} GB not recorded` : `${num(r.recordedGb - r.totalGb)} GB more than the bucket`}
              </p>
            </div>
            <div className={styles.stat}>
              <p className={styles.statLabel}>Not anyone&apos;s</p>
              <p className={styles.statValue}>{num(r.unattributedGb)} GB</p>
              <p className={styles.statHint}>paths that name no file, note or project</p>
            </div>
            <div className={styles.stat}>
              <p className={styles.statLabel}>Unfinished uploads</p>
              <p className={styles.statValue}>{num(r.multipartGb, 2)} GB</p>
              <p className={styles.statHint}>
                {num(r.multipartUploads, 0)} uploads{r.multipartOldestAt && ` · oldest ${date(r.multipartOldestAt)}`}
              </p>
            </div>
          </div>

          {r.warnings.length > 0 && (
            <ul className={styles.warnings}>
              {r.warnings.map((w, i) => (
                <li key={i} className={w.level === 'warning' ? styles.warnItem : styles.infoItem}>
                  <span className="material-symbols-outlined text-base" aria-hidden="true">
                    {w.level === 'warning' ? 'warning' : 'info'}
                  </span>
                  <span><span className={styles.srOnly}>{w.level === 'warning' ? 'Warning: ' : 'Note: '}</span>{w.message}</span>
                </li>
              ))}
            </ul>
          )}

          {r.factors.length > 0 && (
            <>
              <h4 className={styles.subTitle}>What a GB uploaded leaves in S3</h4>
              <p className={toChange.length ? styles.adviceSummaryChange : styles.adviceSummary}>
                {toChange.length > 0
                  ? `${toChange.length} storage factor${toChange.length === 1 ? '' : 's'} in Unit prices differ${toChange.length === 1 ? 's' : ''} from what the bucket holds: ${toChange.map((x) => x.f.label).join(', ')}.`
                  : 'Every storage factor with enough files matches what the bucket holds.'}
                {unsaved.length > 0 && ` ${unsaved.map((x) => x.f.label).join(', ')}: in the form, click Save prices to keep ${unsaved.length === 1 ? 'it' : 'them'}.`}
              </p>
              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr><th>Type</th><th>Files</th><th>Original GB</th><th>Converted GB</th><th>Measured</th><th>Unit prices</th><th>Suggestion</th></tr>
                  </thead>
                  <tbody>
                    {factorRows.map(({ f, key, saved, advice }) => (
                      <tr key={f.type}>
                        <td className={styles.strong}>{f.label}</td>
                        <td>{num(f.files, 0)}</td>
                        <td>{num(f.rawGb)}</td>
                        <td>{num(f.derivedGb)}</td>
                        <td className={styles.strong}>× {num(f.measured, 2)}</td>
                        <td>× {num(saved, 2)}</td>
                        <td>
                          <FactorSuggestion
                            advice={advice}
                            onUse={key && factorControl && advice.kind === 'change' ? () => factorControl.onUse(key, advice.value) : undefined}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className={styles.small}>
                Measured = what a type&apos;s files hold in S3, original and converted (LAZ / harmonize left
                out), ÷ their originals. The calculator multiplies each upload by the factor in Unit
                prices. A type is judged from {FACTOR_MIN_FILES} files on, and a factor within {FACTOR_TOLERANCE * 100}% is kept.
                {factorControl ? ' “Use” puts the measured value into Unit prices below; nothing changes until you click Save prices.' : ''}
              </p>
            </>
          )}

          <h4 className={styles.subTitle}>Where the bucket and the records disagree</h4>
          {deleteError && <p className={styles.warn} role="alert">The folders could not be deleted: {deleteError}</p>}
          {deletion?.running && (
            <p className={styles.progress} role="status">
              <span className="material-symbols-outlined animate-spin text-sm">progress_activity</span>
              Deleting… {num(deletion.done, 0)} of {num(deletion.total, 0)} folders
            </p>
          )}
          {deletion && !deletion.running && (
            <p className={deletion.failed ? styles.warn : styles.done}>
              Deleted {num(deletion.deleted, 0)} folder{deletion.deleted === 1 ? '' : 's'} ({num(deletion.deletedGb, 2)} GB) from S3.
              {deletion.skipped > 0 && ` ${num(deletion.skipped, 0)} skipped: they belong to something now.`}
              {deletion.failed > 0 && ` ${num(deletion.failed, 0)} failed: ${deletion.error ?? 'unknown error'}.`}
              {' '}The figures above are from the check; check again to refresh them.
            </p>
          )}
          {/* A finished check replaces the lists on the server: start again from its first page. */}
          <FolderLists
            key={check?.finishedAt ?? 'running'}
            counts={{ orphans: r.orphanCount, missing: r.missingCount, differs: r.differsCount }}
            orphans={{
              count: r.deletableCount,
              gb: r.deletableGb,
              busy: busy || deleting,
              progress: deletion?.done ?? 0,
              onDelete: (target) => { setDeleteError(null); void deleteOrphans({ variables: target }); },
            }}
          />

          <h4 className={styles.subTitle}>By storage class</h4>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead><tr><th>Class</th><th>GB</th><th>$/month</th></tr></thead>
              <tbody>
                {r.byClass.map((c) => (
                  <tr key={c.key}>
                    <td className={styles.strong}>{CLASS_LABEL[c.key] ?? c.key}</td>
                    <td>{num(c.gb)}</td>
                    <td>{usd(c.usdMonthly)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.noncurrentGb > 0 && <p className={styles.small}>{num(r.noncurrentGb, 2)} GB of it are old object versions.</p>}
        </>
      )}
    </div>
  );
};

export default BucketCheck;
